import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Box, Text, useMedia } from '../../../components/Boneyard';
import moment from '@g4/moment-utc';
import _ from 'lodash';

import TEAM_COLORS from './teamColors';
import {
  white,
  waterBlue,
  gradientOrange,
} from '@g4/boneyard/lib/theme/colors';

import {
  LIMITED_SEATS_LABEL,
  GAME_SELECTED_LABEL,
  SOLD_OUT_GAME_LABEL,
} from '../automationHooks.json';

import { grey300, fullBlack, black } from '../../../theme/theme/colors';

const propTypes = {
  theme: PropTypes.object.isRequired,
  isLimited: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  date: PropTypes.string,
  opponent: PropTypes.shape({
    city: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  raidersCityLabel: PropTypes.string.isRequired,
  raidersTeamNameLabel: PropTypes.string.isRequired,
  raidersLimitedSeatsLabel: PropTypes.string.isRequired,
  raidersSoldOutLabel: PropTypes.string.isRequired,
  raidersVSLabel: PropTypes.string.isRequired,
  gameSelectedLabel: PropTypes.string,
  selectedGameIndex: PropTypes.number,
  highlightedShowIndex: PropTypes.number,
  automationHook: PropTypes.string,
};

const defaultProps = {
  isLimited: false,
  isSoldOut: false,
};

const Container = styled(Box).attrs(({ theme, isGameSelected }) => ({
  w: theme.toRem(400),
  h: theme.toRem(175),
  d: 'flex',
  direction: 'column',
  b: isGameSelected
    ? `4px solid ${gradientOrange}`
    : `solid ${theme.toRem(1)} ${
        theme.raiders.gameCard.colors.containerBorder
      }`,
  borderRadius: 10,
  bt: isGameSelected ? `22px solid ${gradientOrange}` : 'none',
}))`
  background-image: linear-gradient(
    to bottom,
    ${({ theme }) => theme.raiders.gameCard.colors.containerGradient1},
    ${({ theme }) => theme.raiders.gameCard.colors.containerGradient2}
  );
  background-color: white;
`;

const LimitedSeats = styled(Box).attrs(({ theme }) => ({
  w: theme.toRem(0),
  h: theme.toRem(0),
  position: 'absolute',
}))`
  top: calc(100% - ${({ theme }) => theme.toRem(10)});
  left: calc(50% - ${({ theme }) => theme.toRem(45)});
`;

const LimitedSeatsText = styled(Text).attrs({
  size: 10,
  weight: 500,
  color: white,
  uppercase: true,
})`
  background-color: ${waterBlue};
  border-radius: 5px;
  width: 90px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SoldOut = styled(Box).attrs(({ theme }) => ({
  w: theme.toRem(0),
  h: theme.toRem(0),
  position: 'absolute',
  zIndex: 1,
}))`
  top: calc(50% - ${({ theme }) => theme.toRem(35)});
  left: calc(50% - ${({ theme }) => theme.toRem(140)});
`;

const SoldOutText = styled(Text).attrs({
  weight: 500,
  color: `${grey300}`,
  uppercase: true,
})`
  background-color: ${fullBlack};
  border-radius: 11px;
  width: ${props => (props.isMobile ? '240px' : '280px')};
  height: 70px;
  margin: ${props => (props.isMobile ? 'auto 20px' : 'auto')};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transform: rotate(-11deg);
`;

const TeamContainer = styled(Box).attrs(({ theme, isStadium }) => ({
  h: theme.toRem(60),
  w: '40%',
  d: 'flex',
  direction: 'column',
  alignItems: `flex-${isStadium ? 'end' : 'start'}`,
  overflow: 'hidden',
}))`
  clip-path: ${props =>
    props.isStadium
      ? 'polygon(25% 0, 100% 0, 100% 100%, 0% 100%)'
      : 'polygon(0 0, 100% 0, 75% 100%, 0% 100%)'};
`;

const Stripe = styled(Box).attrs(({ theme, color, isStadium }) => ({
  h: color ? '10%' : '50%',
  w: '100%',
  d: 'flex',
  mt: color ? 1 : 0,
  alignItems: 'center',
  backgroundColor: color || theme.raiders.gameCard.colors.black,
  b: color
    ? 'none'
    : `solid ${theme.toRem(1)} ${theme.raiders.gameCard.colors.white}`,
  justifyContent: `flex-${isStadium ? 'end' : 'start'}`,
  pr: isStadium ? 0 : 5,
  pl: isStadium ? 5 : 0,
}))``;

const StripeText = styled(Text).attrs({
  color: white,
})`
  margin: ${props => (props.isMobile ? '0 5px' : '0 auto')};
  font-weight: 600;
`;

const SelectedGameText = styled(Box)`
  margin: -20px auto;
  color: ${black};
  font-weight: 600;
  text-transform: uppercase;
`;

const GameCard = ({
  theme,
  isLimited,
  isSoldOut,
  date,
  opponent: { city: opponentCity, name: opponentName },
  raidersCityLabel,
  raidersTeamNameLabel,
  raidersLimitedSeatsLabel,
  raidersSoldOutLabel,
  raidersVSLabel,
  gameSelectedLabel,
  selectedGameIndex,
  highlightedShowIndex,
}) => {
  const mq = useMedia();
  const isMobile = mq.lte('sm');

  const gameCardTheme = theme?.raiders?.gameCard ?? {};

  let opponentColorKey = `${opponentCity}_${opponentName}`
    .replace(/ /, '_')
    .toLowerCase();

  const raidersColorKey = `${raidersCityLabel}_${raidersTeamNameLabel}`
    .replace(/ /, '_')
    .toLowerCase();

  const weekday = moment(date).format('dddd');
  const month = moment(date).format('MMMM');
  const day = moment(date).date();

  const isGameSelected = () => {
    return highlightedShowIndex === selectedGameIndex && !isSoldOut;
  };

  return (
    <Container alignItems="center" isGameSelected={isGameSelected()}>
      {isGameSelected() && (
        <SelectedGameText automationHook={GAME_SELECTED_LABEL.hook}>
          {gameSelectedLabel}
        </SelectedGameText>
      )}
      {isLimited && (
        <LimitedSeats>
          <LimitedSeatsText automationHook={LIMITED_SEATS_LABEL.hook}>
            {raidersLimitedSeatsLabel}
          </LimitedSeatsText>
        </LimitedSeats>
      )}
      {isSoldOut && (
        <SoldOut>
          <SoldOutText
            size={isMobile ? 42 : 48}
            automationHook={SOLD_OUT_GAME_LABEL.hook}
            isMobile={isMobile}
          >
            {raidersSoldOutLabel}
          </SoldOutText>
        </SoldOut>
      )}
      <Box h={theme.toRem(100)} w="100%" d="flex" alignItems="center">
        <TeamContainer>
          <Stripe>
            <StripeText size={isMobile ? 12 : 16} isMobile={isMobile}>
              {_.startCase(opponentCity)}
            </StripeText>
          </Stripe>
          <Stripe primary color={TEAM_COLORS[opponentColorKey]?.primary} />
          <Stripe secondary color={TEAM_COLORS[opponentColorKey]?.secondary} />
        </TeamContainer>
        <Box
          h="100%"
          d="flex"
          flex="1"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            size={isMobile ? 24 : 36}
            color={gameCardTheme.colors.vs}
            uppercase
          >
            {raidersVSLabel}
          </Text>
        </Box>
        <TeamContainer isStadium>
          <Stripe isStadium>
            <StripeText size={isMobile ? 12 : 16} isMobile={isMobile} isStadium>
              {raidersCityLabel}
            </StripeText>
          </Stripe>
          <Stripe
            isStadium
            primary
            color={TEAM_COLORS[raidersColorKey]?.primary}
          />
          <Stripe
            isStadium
            secondary
            color={TEAM_COLORS[raidersColorKey]?.secondary}
          />
        </TeamContainer>
      </Box>
      <Box
        h={theme.toRem(1)}
        w="80%"
        backgroundColor={gameCardTheme.colors.separator}
      />
      <Box flex="1" display="flex" direction="column">
        <Box h="40%" d="flex" alignItems="flex-end" justifyContent="center">
          <Text size={14} color={gameCardTheme.colors.weekdayText} uppercase>
            {weekday}
          </Text>
        </Box>
        <Box h="60%" d="flex" alignItems="flex-start" justifyContent="center">
          <Text size={21} color={gameCardTheme.colors.monthDayText} uppercase>
            {month} {day}
          </Text>
        </Box>
      </Box>
    </Container>
  );
};

GameCard.propTypes = propTypes;

GameCard.defaultProps = defaultProps;

export default withTheme(GameCard);
